<script lang="ts">
export default {
  inheritAttrs: false,
}
</script>

<script lang="ts" setup>
const props = withDefaults(
  defineProps<{
    theme?:
      | 'default'
      | 'default-dense'
      | 'unstyled'
      | 'dark'
      | 'dark-dense'
      | 'prose'
      | 'text'
      | 'text-accent'
      | 'text-standalone'
      | 'text-accent-standalone'
    autoDisable?: boolean
  }>(),
  {
    theme: 'default',
    dense: false,
    autoDisable: false,
  },
)

const inheritedDisabled = useDisabled()
const attrs = useAttrs()
const derivedDisabled = computed(() =>
  props.autoDisable ? inheritedDisabled.value : 'disabled' in attrs && attrs.disabled !== false,
)
</script>

<template>
  <ActionTrigger
    :disabled="derivedDisabled"
    v-bind="$attrs"
    :class="{
      'inline-flex items-center rounded border border-grey-200 bg-white text-sm font-medium leading-none text-blue-800 no-underline hover:border-grey-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-800 active:border-blue-500 active:bg-blue-50':
        theme === 'default' || theme === 'default-dense',
      'inline-flex items-center rounded bg-blue-800 text-sm font-medium leading-none text-grey-50 no-underline hover:bg-blue-700 hover:text-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-800 active:bg-blue-900 active:text-blue-200':
        theme === 'dark' || theme === 'dark-dense',
      'px-6 py-3.5': theme === 'default' || theme === 'dark',
      'px-3 py-2.5': theme === 'default-dense' || theme === 'dark-dense',
      'flex rounded border border-grey-200 bg-white px-6 py-3.5 text-left text-sm font-normal leading-normal text-black no-underline hover:border-grey-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-800 active:border-blue-500 active:bg-blue-50':
        theme === 'prose',
      'inline-flex items-center font-medium text-blue-800 underline decoration-1 underline-offset-2 hover:text-blue-700 focus-visible:decoration-blue-400 focus-visible:decoration-4 focus-visible:outline-none active:text-blue-900':
        theme === 'text-accent' || theme === 'text-accent-standalone',
      'inline-flex items-center font-medium underline decoration-1 underline-offset-2 focus-visible:decoration-blue-400 focus-visible:decoration-4 focus-visible:outline-none':
        theme === 'text' || theme === 'text-standalone',
      'pointer-events-none opacity-50': derivedDisabled,
      'text-sm': theme === 'text-standalone' || theme === 'text-accent-standalone',
    }"
  >
    <slot v-if="$slots.dropdown" name="dropdown" slot="dropdown" />
    <span
      data-icon-before
      class="flex-shrink-0"
      :class="{
        '[&:not(:last-child)]:pr-2.5':
          theme !== 'unstyled' && theme !== 'default-dense' && theme !== 'dark-dense',
        '[&:not(:last-child)]:pr-2': theme === 'default-dense' || theme === 'dark-dense',
      }"
      v-if="$slots['icon-before']"
    >
      <slot name="icon-before" />
    </span>
    <span v-if="$slots.default"><slot /></span>
    <span
      data-icon-after
      class="ml-auto flex-shrink-0"
      :class="{
        '[&:not(:first-child)]:pl-2.5':
          theme !== 'unstyled' && theme !== 'default-dense' && theme !== 'dark-dense',
        '[&:not(:first-child)]:pl-2': theme === 'default-dense' || theme === 'dark-dense',
      }"
      v-if="$slots['icon-after']"
    >
      <slot name="icon-after" />
    </span>
  </ActionTrigger>
</template>
